import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO = "https://customer-assets.emergentagent.com/job_e31510f3-0985-434c-b5ba-05b91fac6a7c/artifacts/ywyzdsaz_logomarca.jpeg";

export default function Header({ cinema = false }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    // Close drawer on route change
    useEffect(() => { setOpen(false); }, [location.pathname]);

    // Lock body scroll while drawer is open
    useEffect(() => {
        if (open) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        }
    }, [open]);

    const navItems = [
        { to: "/home", label: "Início", test: "nav-home" },
        { to: "/jornadas", label: "Jornadas", test: "nav-journeys" },
        { to: "/biblioteca", label: "Biblioteca", test: "nav-library" },
        { to: "/mentores", label: "Mentores", test: "nav-mentors" },
        { to: "/planos", label: "Planos", test: "nav-plans" },
        ...(user ? [{ to: "/perfil", label: "Meu espaço", test: "nav-profile" }] : []),
        ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", test: "nav-admin" }] : []),
    ];

    const linkCls = ({ isActive }) =>
        `font-display text-sm tracking-wide transition-colors ${
            cinema
                ? `${isActive ? "text-white" : "text-white/60 hover:text-white"}`
                : `${isActive ? "text-[#1A1A1A]" : "text-[#5C5C5C] hover:text-[#1A1A1A]"}`
        }`;

    const handleLogout = async () => { await logout(); setOpen(false); navigate("/"); };

    return (
        <>
            <header
                data-testid="site-header"
                className={`sticky top-0 z-40 ${cinema ? "glass-dark" : "glass"}`}
            >
                <div className="max-w-[1400px] mx-auto px-4 md:px-10 h-[64px] md:h-[72px] flex items-center justify-between gap-3">
                    {/* Logo — compact on mobile, full on md+ */}
                    <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 md:gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-xl overflow-hidden shrink-0 ${cinema ? "" : "bg-black"} flex items-center justify-center`}>
                            <img src={LOGO} alt="IMQ" className="w-full h-full object-cover" />
                        </div>
                        {/* Mobile: short brand name in single column */}
                        <div className="md:hidden leading-tight min-w-0">
                            <div className={`font-display font-semibold text-sm ${cinema ? "text-white" : "text-[#1A1A1A]"} tracking-tight truncate`}>
                                Medicina Quântica
                            </div>
                        </div>
                        {/* Desktop: full overline + brand name */}
                        <div className="hidden md:block leading-tight">
                            <div className={`overline ${cinema ? "text-white/70" : "text-[#5C5C5C]"}`}>Instituto de</div>
                            <div className={`font-display font-semibold ${cinema ? "text-white" : "text-[#1A1A1A]"} tracking-tight`}>
                                Medicina Quântica
                            </div>
                        </div>
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-9">
                        {navItems.map(n => (
                            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={n.test}>{n.label}</NavLink>
                        ))}
                    </nav>

                    {/* Desktop actions */}
                    <div className="hidden md:flex items-center gap-3 shrink-0">
                        {user ? (
                            <>
                                <div className={`text-right leading-tight ${cinema ? "text-white/80" : "text-[#5C5C5C]"}`}>
                                    <div className="text-xs">Olá,</div>
                                    <div className="font-display text-sm">{user.name.split(" ")[0]}</div>
                                </div>
                                <button
                                    data-testid="logout-btn"
                                    onClick={handleLogout}
                                    className={cinema ? "btn-ghost !bg-white/10 !text-white !border-white/15" : "btn-ghost"}
                                >
                                    Sair
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" data-testid="header-login" className={cinema ? "btn-ghost !bg-white/10 !text-white !border-white/15" : "btn-ghost"}>
                                    Entrar
                                </Link>
                                <Link to="/cadastro" data-testid="header-register" className="btn-primary whitespace-nowrap">
                                    Começar grátis
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        data-testid="mobile-menu-open"
                        aria-label="Abrir menu"
                        className={`md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full shrink-0 ${
                            cinema
                                ? "bg-white/10 text-white border border-white/15"
                                : "bg-white text-[#1A1A1A] border border-black/10"
                        }`}
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                </div>
            </header>

            {/* Mobile drawer */}
            <div
                data-testid="mobile-menu"
                className={`md:hidden fixed inset-0 z-50 transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            >
                {/* Backdrop */}
                <div
                    onClick={() => setOpen(false)}
                    className="absolute inset-0 bg-[#0A0A0A]/60 backdrop-blur-sm"
                    data-testid="mobile-menu-backdrop"
                />
                {/* Panel */}
                <aside
                    className={`absolute top-0 right-0 h-full w-[88%] max-w-[380px] bg-[#F8F8F5] shadow-2xl flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
                >
                    {/* Header of the drawer */}
                    <div className="flex items-center justify-between px-5 h-[64px] border-b border-black/5">
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl overflow-hidden bg-black flex items-center justify-center">
                                <img src={LOGO} alt="IMQ" className="w-full h-full object-cover" />
                            </div>
                            <div className="leading-tight">
                                <div className="overline text-[#5C5C5C] text-[9px]">Instituto de</div>
                                <div className="font-display font-semibold text-[#1A1A1A] tracking-tight text-sm">Medicina Quântica</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-menu-close"
                            aria-label="Fechar menu"
                            className="w-10 h-10 rounded-full flex items-center justify-center text-[#1A1A1A] hover:bg-black/5 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* User block (if logged) */}
                    {user && (
                        <div className="px-5 py-4 border-b border-black/5">
                            <div className="overline text-[#5C5C5C]">Conectado como</div>
                            <div className="font-display text-lg text-[#1A1A1A] mt-1 truncate">{user.name}</div>
                            <div className="font-body text-xs text-[#5C5C5C] truncate">{user.email}</div>
                        </div>
                    )}

                    {/* Nav items */}
                    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                        {navItems.map(n => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`mobile-${n.test}`}
                                className={({ isActive }) =>
                                    `flex items-center justify-between px-4 py-3.5 rounded-2xl font-display text-base transition-colors ${
                                        isActive
                                            ? "bg-[#1A1A1A] text-white"
                                            : "text-[#1A1A1A] hover:bg-black/5"
                                    }`
                                }
                            >
                                <span>{n.label}</span>
                                <ArrowRight className="w-4 h-4 opacity-50" />
                            </NavLink>
                        ))}
                    </nav>

                    {/* Footer actions */}
                    <div className="p-4 border-t border-black/5 space-y-2.5 bg-white">
                        {user ? (
                            <button
                                onClick={handleLogout}
                                data-testid="mobile-logout"
                                className="btn-ghost w-full justify-center"
                            >
                                Sair da conta
                            </button>
                        ) : (
                            <>
                                <Link
                                    to="/cadastro"
                                    data-testid="mobile-register"
                                    className="btn-primary w-full justify-center"
                                >
                                    Começar 7 dias grátis
                                </Link>
                                <Link
                                    to="/login"
                                    data-testid="mobile-login"
                                    className="btn-ghost w-full justify-center"
                                >
                                    Já tenho conta · Entrar
                                </Link>
                            </>
                        )}
                    </div>
                </aside>
            </div>
        </>
    );
}
